import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { GoogleService } from '../GoogleService';
import { ToastService } from '@iamsoftware/react-hooks';
import { useDataTable } from '@iamsoftware/react-hooks';
import { AgreementService } from '../../hop-dong/quan-ly-hop-dong/AgreementService';
import { Button } from 'primereact/button';
import { UploadExcel } from '../UploadExcel';

export const TaiLieuNhap: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayUploadExcel, setDisplayUploadExcel] = useState(false);
  const dataKey = 'agreementId';

  // Templates
  const statusBodyTemplate = (rowData: any) => {
    return (
      <div className="p-2 bg-gray-50" style={{ minWidth: 120 }}>
        <div className="mb-1"><Tag value="Nháp" severity="secondary" /></div>
        {rowData.isImportantInd === 'Y' || rowData.isImportantInd === true ? (
          <div className="flex align-items-center gap-2 mb-1">
            <i className="pi pi-bookmark-fill" style={{ color: '#f59e0b' }}></i>
            <span className="text-orange-500 text-sm">Quan trọng</span>
          </div>
        ) : null}
        {rowData.isUrgentInd === 'Y' || rowData.isUrgentInd === true ? (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-bolt" style={{ color: '#ef4444' }}></i>
            <span className="text-red-500 text-sm">Khẩn cấp</span>
          </div>
        ) : null}
      </div>
    );
  };

  const documentTypeBodyTemplate = (rowData: any) => {
    return rowData.documentTypeName || 'N/A';
  };

  const dateBodyTemplate = (rowData: any) => {
    if (!rowData.lastUpdatedStamp) return 'N/A';
    return new Date(rowData.lastUpdatedStamp).toLocaleString('vi-VN');
  };

  const mainFileBodyTemplate = (rowData: any) => {
    if (!rowData.mainFile) return <span className="text-gray-500">Chưa có file</span>;
    return (
      <div className="flex align-items-center gap-2">
        <i className={`pi ${rowData.mainFile.fileFormat === 'pdf' ? 'pi-file-pdf' : 'pi-file-word'} text-xl`}></i>
        <div>
          <div className="font-medium">{rowData.mainFile.fileName}</div>
        </div>
      </div>
    );
  };

  const fileCountBodyTemplate = (rowData: any) => {
    return (
      <Tag value={`${rowData.fileCount || 0} file`} severity="info" />
    );
  };

  const handleCreateNew = async () => {
    try {
      const res = await GoogleService.createEmpty();
      if (res && res.success === 'Y') {
        navigate(`/quan-ly-tai-lieu/tai-lieu/${res.agreementId}`);
      } else {
        ToastService.error(res?.error || 'Không thể tạo hợp đồng mới');
      }
    } catch (error: any) {
      console.error('Error creating document:', error);
      ToastService.error('Có lỗi xảy ra khi tạo hợp đồng');
    }
  };

  const handleView = (rowData: any) => {
    navigate(`/quan-ly-tai-lieu/tai-lieu/${rowData.agreementId}?mode=view`);
  };

  const handleEdit = (rowData: any) => {
    navigate(`/quan-ly-tai-lieu/tai-lieu/${rowData.agreementId}`);
  };

  const handleSign = (rowData: any) => {
    try {
      AgreementService.signAgreement(rowData.agreementId).then(res => {
        if (res && res.success === 'Y') {
          navigate(`/quan-ly-tai-lieu/tai-lieu-quan-ly`);
        } else {
          ToastService.error(res?.error || 'Không thể ký hợp đồng');
        }
      });
    } catch (e) {
      ToastService.error('Có lỗi khi ký hợp đồng');
    }
  };

  // Removed old view files dialog/download; eye button will navigate to detail screen

  const handleDelete = async (rowData: any) => {
    try {
      const res = await AgreementService.moveToTrash(rowData.agreementId);
      if (res && res.success === 'Y') {
        ToastService.success('Đã chuyển vào thùng rác');
        reloadLazyData();
      } else {
        ToastService.error(res?.error || 'Không thể chuyển vào thùng rác');
      }
    } catch (e) {
      ToastService.error('Có lỗi khi chuyển vào thùng rác');
    }
  };

  const { render: renderDataTable, reloadLazyData } = useDataTable({
    tableHeader: (
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <span>Tài liệu nháp</span>
        <div className="flex gap-2">
          <Button
            label="Upload Excel"
            icon="pi pi-upload"
            size="small"
            severity="help"
            onClick={() => setDisplayUploadExcel(true)}
          />
        </div>
      </div>
    ),
    dataKey,
    columns: [
      {
        field: 'agreementName',
        header: 'Tài liệu',
        width: 100,
        matchMode: 'contains',
        dataType: 'custom',
        customCell(rowData: any): any {
          return <span className="underline text-primary" onClick={() => handleView(rowData)} title="Click để xem chi tiết">{rowData.agreementName}</span>
        }
      },
      { field: 'agreementPseudoId', header: 'Mã tài liệu', width: 120, matchMode: 'contains' },
      { field: 'agreementTypeName', header: 'Loại tài liệu', width: 150, matchMode: 'contains' },
      {
        field: 'statusDescription',
        header: 'Trạng thái',
        width: 120,
        dataType: 'custom',
        customCell(rowData: any): any {
          const isUrgent = rowData.isUrgent === 'Y';
          const isImportant = rowData.isImportant === 'Y';
          return (
            <div className="flex align-items-center gap-2 flex-wrap">
              {isUrgent && (
                <span className="flex align-items-center gap-1 text-orange-500 text-sm" title="Tài liệu cần xử lý gấp">
                  <i className="pi pi-bolt"></i>
                  <span>Khẩn cấp</span>
                </span>
              )}
              {isImportant && (
                <span className="flex align-items-center gap-1 text-indigo-500 text-sm" title="Tài liệu quan trọng">
                  <i className="pi pi-bookmark"></i>
                  <span>Quan trọng</span>
                </span>
              )}
            </div>
          );
        }
      },
      {
        field: 'mainContent',
        header: 'File chính',
        width: 200,
        dataType: 'custom',
        customCell(rowData: any): any {
          if (!rowData.mainFileName && !rowData.mainContentLocation) return <span className="text-gray-500">Chưa có file</span>;
          const isPdf = rowData.mainContentType === 'application/pdf';
          const isWord = rowData.mainContentType?.includes('word') || rowData.mainContentType?.includes('document');
          const iconClass = isPdf ? 'pi-file-pdf' : (isWord ? 'pi-file-word' : 'pi-file');
          return (
            <div className="flex align-items-center gap-2">
              <i className={`pi ${iconClass} text-xl`}></i>
              <div>
                <div className="font-medium">{rowData.mainFileName}</div>                
              </div>
            </div>
          );
        }
      },
      {
        field: 'contentCount',
        header: 'Số file',
        width: 100,
        dataType: 'custom',
        customCell(rowData: any): any {
          return (
            <Tag value={`${rowData.contentCount || 0} file`} severity="info" />
          );
        }
      },
      {
        field: 'expiryDate',
        header: 'Thời gian',
        width: 180,
        dataType: 'custom',
        customCell(rowData: any): any {
          const formatLocal = (value?: any) => {
            if (!value) return null;
            const d = new Date(value);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            const HH = String(d.getHours()).padStart(2, '0');
            const MM = String(d.getMinutes()).padStart(2, '0');
            const SS = String(d.getSeconds()).padStart(2, '0');
            return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
          };

          const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : 'Không có thời hạn ký';
          const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

          return (
            <div className="text-sm">
              <div className="flex align-items-center gap-2 mb-1">
                <i className="pi pi-hourglass" />
                <span>{expiryText}</span>
              </div>
              {updatedText && (
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-clock" />
                  <span>{updatedText}</span>
                </div>
              )}
            </div>
          );
        }
      },
    ],
    indexColumnWidth: 45,
    actionColumnWidth: 140,
    getList: lazyLoadEvent => {
      return AgreementService.getAgreementList(lazyLoadEvent, 'AGT_DRAFT').then(result => {
        setTotalRecords(result.totalRecords)
        return result;
      });
    },
    initActions(items: Array<any>) {
      if (items) {
        items.forEach(item => {
          item.actions = [
            { icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => handleView(item) },
            { icon: 'pi pi-user-edit', title: 'Ký', className: 'p-button-success', command: () => handleSign(item) },
            { icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => handleEdit(item) },
            { icon: 'pi pi-trash', title: 'Xóa', className: 'p-button-danger', command: () => handleDelete(item) }
          ];
        });
      }
    }
  });

  return (
    <div className="card border-none" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)' }}>
      <Toast ref={toast} />
      {renderDataTable()}
      <UploadExcel display={displayUploadExcel} setDisplay={setDisplayUploadExcel} reloadLazyData={reloadLazyData} />
    </div>
  );
};

